import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ID } from '../../../../types/aiTypes';

type Props = {
  endpoints: any[];
  loading?: boolean;
  onAdd: () => void;
  onEdit: (endpoint: any) => void;
  onDelete: (id: ID) => void;
  onRefresh: () => void;
};

const healthColor = (
  status?: string
): 'success' | 'warning' | 'error' | 'default' => {
  const s = String(status || '').toLowerCase();
  if (s === 'healthy') return 'success';
  if (s === 'degraded') return 'warning';
  if (s === 'unhealthy' || s === 'failed') return 'error';
  return 'default';
};

const AgentA2AEndpointListPanel: React.FC<Props> = ({
                                                      endpoints,
                                                      loading,
                                                      onAdd,
                                                      onEdit,
                                                      onDelete,
                                                      onRefresh,
                                                    }) => {
  const showInitialLoading = !!loading && endpoints.length === 0;

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">A2A Endpoints</Typography>
          <Typography variant="caption" color="text.secondary">
            Remote agents reachable over the A2A protocol
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={showInitialLoading}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAdd}
          >
            Add
          </Button>
        </Stack>
      </Stack>

      {showInitialLoading ? (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading endpoints...
          </Typography>
        </Box>
      ) : null}

      {endpoints.length ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Base URL</TableCell>
              <TableCell>External</TableCell>
              <TableCell>Health</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {endpoints.map((ep) => (
              <TableRow key={String(ep.id)} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {ep.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ep.slug}
                  </Typography>
                </TableCell>

                <TableCell sx={{ maxWidth: 260, wordBreak: 'break-all' }}>
                  {ep.base_url}
                </TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={ep.is_external ? 'warning' : 'default'}
                    label={ep.is_external ? 'external' : 'internal'}
                  />
                </TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    color={healthColor(ep.health_status)}
                    label={String(ep.health_status || 'unknown')}
                  />
                </TableCell>

                <TableCell>{String(!!ep.is_active)}</TableCell>

                <TableCell align="right">
                  <Button size="small" onClick={() => onEdit(ep)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete A2A endpoint "${ep.name || ep.slug}"?`
                        )
                      ) {
                        onDelete(ep.id);
                      }
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : !loading ? (
        <Typography variant="body2" color="text.secondary">
          No A2A endpoints registered.
        </Typography>
      ) : null}
    </Paper>
  );
};

export default AgentA2AEndpointListPanel;
